import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LanguageSelector from './LanguageSelector';

const { FiImage, FiMenu, FiX, FiLayers, FiZap } = FiIcons;

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const isActive = (path) => location.pathname === path || location.pathname.endsWith(path);

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="bg-blue-600 p-2 rounded-lg">
              <SafeIcon icon={FiImage} className="text-white text-xl" />
            </div>
            <div>
              <span className="text-xl font-bold text-gray-900">{t('header.brandName')}</span>
              <div className="text-xs text-gray-500">{t('header.brandSubtitle')}</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6 items-center">
            <div className="relative group">
              <button className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors flex items-center">
                <SafeIcon icon={FiImage} className="mr-1 text-xs" />
                {t('navigation.resizeTools')}
                <SafeIcon icon={FiMenu} className="ml-1 text-xs" />
              </button>
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-1">
                  <Link
                    to="/"
                    className={`block px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      isActive('/') ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                    }`}
                  >
                    {t('navigation.singleImageResize')}
                  </Link>
                  <Link
                    to="/batch-resize"
                    className={`block px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      isActive('/batch-resize') ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                    }`}
                  >
                    {t('navigation.batchImageResize')}
                  </Link>
                </div>
              </div>
            </div>

            <div className="relative group">
              <button className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-green-600 hover:bg-gray-50 transition-colors flex items-center">
                <SafeIcon icon={FiZap} className="mr-1 text-xs" />
                {t('navigation.compressTools')}
                <SafeIcon icon={FiMenu} className="ml-1 text-xs" />
              </button>
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-1">
                  <Link
                    to="/compress"
                    className={`block px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      isActive('/compress') ? 'text-green-600 bg-green-50' : 'text-gray-700'
                    }`}
                  >
                    {t('navigation.singleImageCompress')}
                  </Link>
                  <Link
                    to="/batch-compress"
                    className={`block px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      isActive('/batch-compress') ? 'text-green-600 bg-green-50' : 'text-gray-700'
                    }`}
                  >
                    {t('navigation.batchImageCompress')}
                  </Link>
                </div>
              </div>
            </div>

            <Link
              to="/features"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/features') 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
              }`}
            >
              {t('navigation.features')}
            </Link>

            <Link
              to="/how-it-works"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/how-it-works') 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
              }`}
            >
              {t('navigation.howItWorks')}
            </Link>

            <Link
              to="/privacy"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/privacy') 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
              }`}
            >
              {t('navigation.privacy')}
            </Link>

            {/* Language Selector */}
            <LanguageSelector />
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-50"
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-xl" />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden py-4 border-t border-gray-200"
          >
            <div className="flex flex-col space-y-2">
              <div className="px-3 py-2 text-sm font-medium text-gray-500 uppercase tracking-wide">
                {t('navigation.resizeTools')}
              </div>
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive('/') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                {t('navigation.singleImageResize')}
              </Link>
              <Link
                to="/batch-resize"
                onClick={() => setIsMenuOpen(false)}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive('/batch-resize') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                {t('navigation.batchImageResize')}
              </Link>
              
              <div className="px-3 py-2 text-sm font-medium text-gray-500 uppercase tracking-wide mt-4">
                {t('navigation.compressTools')}
              </div>
              <Link
                to="/compress"
                onClick={() => setIsMenuOpen(false)}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive('/compress') 
                    ? 'text-green-600 bg-green-50' 
                    : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
                }`}
              >
                {t('navigation.singleImageCompress')}
              </Link>
              <Link
                to="/batch-compress"
                onClick={() => setIsMenuOpen(false)}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive('/batch-compress') 
                    ? 'text-green-600 bg-green-50' 
                    : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
                }`}
              >
                {t('navigation.batchImageCompress')}
              </Link>

              <div className="border-t border-gray-200 mt-4 pt-4">
                <Link
                  to="/features"
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors block ${
                    isActive('/features') 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  {t('navigation.features')}
                </Link>
                <Link
                  to="/how-it-works"
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors block ${
                    isActive('/how-it-works') 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  {t('navigation.howItWorks')}
                </Link>
                <Link
                  to="/privacy"
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors block ${
                    isActive('/privacy') 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  {t('navigation.privacy')}
                </Link>
              </div>

              {/* Mobile Language Selector */}
              <div className="border-t border-gray-200 mt-4 pt-4">
                <LanguageSelector isMobile={true} />
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </header>
  );
}

export default Header;